import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  Checkbox,
  CircularProgress,
  Alert,
  IconButton,
  Collapse,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import apiClient from "../utils/apiClient";

const RotationFamilyRulesManager = () => {
  const [rotationType, setRotationType] = useState("three_year");
  const [cropFamilies, setCropFamilies] = useState([]);
  const [crops, setCrops] = useState([]);
  const [coverCrops, setCoverCrops] = useState([]);
  const [expandedFamilies, setExpandedFamilies] = useState({});
  const [isCoverCropsExpanded, setIsCoverCropsExpanded] = useState(false);
  const [rules, setRules] = useState({}); // crop_uuid -> group_number
  const [groupNames, setGroupNames] = useState({});
  const [coverCropWeights, setCoverCropWeights] = useState({}); // crop_uuid -> { [groupNum]: { uuid, value } }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const maxGroups = {
    three_year: 3,
    four_year: 4,
    six_year: 6,
  };

  useEffect(() => {
    fetchData();
  }, [rotationType]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [cropsRes, rulesRes, weightsRes] = await Promise.all([
        apiClient.get("/crops"),
        apiClient.get(`/rotation-family-rules/${rotationType}`),
        apiClient.get(`/cover-crop-rotation-weights/${rotationType}`),
      ]);

      setCrops(cropsRes.data);

      const isCoverCrop = (crop) => crop?.crop_type?.name === "Cover crop";
      const nonCoverCrops = cropsRes.data.filter((c) => !isCoverCrop(c));
      const coverCropList = cropsRes.data
        .filter((c) => isCoverCrop(c))
        .sort((a, b) => a.name.localeCompare(b.name));
      setCoverCrops(coverCropList);
      
      // Build families list from crops (group crops by family)
      const familiesMap = new Map();
      nonCoverCrops.forEach((crop) => {
        if (crop.crop_family) {
          const familyUuid = crop.crop_family.crop_family_uuid;
          if (!familiesMap.has(familyUuid)) {
            familiesMap.set(familyUuid, {
              crop_family_uuid: familyUuid,
              name: crop.crop_family.name,
            });
          }
        }
      });
      
      // Convert map to array and sort by name
      const familiesArray = Array.from(familiesMap.values()).sort((a, b) => {
        const aIsUnknown = a.name?.toLowerCase().includes("ukendt");
        const bIsUnknown = b.name?.toLowerCase().includes("ukendt");
        if (aIsUnknown && !bIsUnknown) return 1; // put unknown last
        if (!aIsUnknown && bIsUnknown) return -1;
        return a.name.localeCompare(b.name);
      });
      setCropFamilies(familiesArray);
      
      // Initialize group names from rules
      const groupNamesMap = {};
      if (rulesRes.data.groups) {
        rulesRes.data.groups.forEach((group) => {
          groupNamesMap[group.group_number] = group.group_name || `Group ${group.group_number}`;
        });
      }
      
      // Fill in default names for missing groups
      for (let i = 1; i <= maxGroups[rotationType]; i++) {
        if (!groupNamesMap[i]) {
          groupNamesMap[i] = `Group ${i}`;
        }
      }
      
      setGroupNames(groupNamesMap);
      
      // Create a map of crop_uuid -> group_number
      const rulesMap = {};
      if (rulesRes.data.groups) {
        rulesRes.data.groups.forEach((group) => {
          if (group.crops) {
            group.crops.forEach((crop) => {
              const fullCrop = cropsRes.data.find((c) => c.crop_uuid === crop.crop_uuid);
              if (fullCrop && !isCoverCrop(fullCrop)) {
                rulesMap[crop.crop_uuid] = group.group_number;
              }
            });
          }
        });
      }
      setRules(rulesMap);

      // Initialize cover crop weights state from API response
      const weightsState = {};
      coverCropList.forEach((c) => {
        weightsState[c.crop_uuid] = {};
        for (let i = 1; i <= maxGroups[rotationType]; i++) {
          weightsState[c.crop_uuid][i] = { uuid: null, value: "" };
        }
      });

      if (weightsRes.data?.groups) {
        weightsRes.data.groups.forEach((g) => {
          const groupNum = g.group_number;
          (g.weights || []).forEach((w) => {
            if (weightsState[w.crop_uuid] && weightsState[w.crop_uuid][groupNum]) {
              weightsState[w.crop_uuid][groupNum] = {
                uuid: w.cover_crop_rotation_weight_uuid,
                value: w.weight,
              };
            }
          });
        });
      }

      setCoverCropWeights(weightsState);
    } catch (err) {
      console.error("Error fetching data:", err);
      setError("Failed to load rotation family rules");
    } finally {
      setLoading(false);
    }
  };

  const handleRotationTypeChange = (e) => {
    setRotationType(e.target.value);
  };

  const handleGroupNameChange = (groupNumber, value) => {
    setGroupNames((prev) => ({
      ...prev,
      [groupNumber]: value,
    }));
  };

  const toggleFamilyExpanded = (familyUuid) => {
    setExpandedFamilies((prev) => ({
      ...prev,
      [familyUuid]: !prev[familyUuid],
    }));
  };

  const toggleCoverCropsExpanded = () => {
    setIsCoverCropsExpanded((prev) => !prev);
  };

  const getFamilyCrops = (familyUuid) => {
    return crops.filter(
      (crop) =>
        crop.crop_family?.crop_family_uuid === familyUuid &&
        crop.crop_type?.name !== "Cover crop"
    );
  };

  const areAllFamilyCropsInGroup = (familyUuid, groupNumber) => {
    const familyCrops = getFamilyCrops(familyUuid);
    if (familyCrops.length === 0) return false;
    return familyCrops.every((crop) => rules[crop.crop_uuid] === groupNumber);
  };

  const isFamilyPartiallyChecked = (familyUuid, groupNumber) => {
    const familyCrops = getFamilyCrops(familyUuid);
    if (familyCrops.length === 0) return false;
    const checkedCount = familyCrops.filter((crop) => rules[crop.crop_uuid] === groupNumber).length;
    return checkedCount > 0 && checkedCount < familyCrops.length;
  };

  const handleFamilyCheckboxChange = (familyUuid, groupNumber) => {
    const familyCrops = getFamilyCrops(familyUuid);
    const allChecked = areAllFamilyCropsInGroup(familyUuid, groupNumber);
    
    setRules((prev) => {
      const newRules = { ...prev };
      if (allChecked) {
        // Uncheck all crops in this family for this group
        familyCrops.forEach((crop) => {
          if (newRules[crop.crop_uuid] === groupNumber) {
            delete newRules[crop.crop_uuid];
          }
        });
      } else {
        // Check all crops in this family for this group
        // First, remove them from any other group
        familyCrops.forEach((crop) => {
          delete newRules[crop.crop_uuid];
        });
        // Then add them to this group
        familyCrops.forEach((crop) => {
          newRules[crop.crop_uuid] = groupNumber;
        });
      }
      return newRules;
    });
  };

  const handleCropCheckboxChange = (cropUuid, groupNumber) => {
    setRules((prev) => {
      const newRules = { ...prev };
      if (newRules[cropUuid] === groupNumber) {
        // Uncheck
        delete newRules[cropUuid];
      } else {
        // Remove from any other group first
        delete newRules[cropUuid];
        // Then add to this group
        newRules[cropUuid] = groupNumber;
      }
      return newRules;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      // Prepare bulk update payload
      const rulesToCreate = [];
      const rulesToUpdate = [];
      const ruleUuidsToDelete = [];

      // Get existing rules to determine what to update vs create
      const existingRulesRes = await apiClient.get(`/rotation-family-rules?rotation_type=${rotationType}`);
      const existingRules = existingRulesRes.data;
      const existingRulesMap = {};
      const isCoverCrop = (crop) => crop?.crop_type?.name === "Cover crop";
      const coverCropUuidSet = new Set(coverCrops.map((c) => c.crop_uuid));
      existingRules.forEach((rule) => {
        existingRulesMap[rule.crop_uuid] = rule;
      });

      // Process each crop
      Object.entries(rules).forEach(([cropUuid, groupNumber]) => {
        const existingRule = existingRulesMap[cropUuid];
        const groupName = groupNames[groupNumber];

        if (existingRule) {
          // Check if update is needed
          if (
            existingRule.group_number !== groupNumber ||
            existingRule.group_name !== groupName
          ) {
            rulesToUpdate.push({
              rotation_family_rule_uuid: existingRule.rotation_family_rule_uuid,
              rotation_type: rotationType,
              group_number: groupNumber,
              group_name: groupName,
              crop_uuid: cropUuid,
            });
          }
        } else {
          // New rule to create
          rulesToCreate.push({
            rotation_type: rotationType,
            group_number: groupNumber,
            group_name: groupName,
            crop_uuid: cropUuid,
          });
        }
      });

      // Find rules to delete (crops that were unassigned)
      existingRules.forEach((rule) => {
        if (coverCropUuidSet.has(rule.crop_uuid)) return; // don't touch cover crops here
        if (!rules[rule.crop_uuid]) {
          ruleUuidsToDelete.push(rule.rotation_family_rule_uuid);
        }
      });

      // Prepare cover crop weight bulk payload (blank = delete)
      const weightsToCreate = [];
      const weightsToUpdate = [];
      const weightUuidsToDelete = [];

      Object.entries(coverCropWeights || {}).forEach(([cropUuid, perGroup]) => {
        Object.entries(perGroup || {}).forEach(([groupNumStr, entry]) => {
          const groupNum = Number(groupNumStr);
          const raw = entry?.value;
          const uuid = entry?.uuid;

          if (raw === "" || raw === null || typeof raw === "undefined") {
            if (uuid) weightUuidsToDelete.push(uuid);
            return;
          }

          const value = Number(raw);
          if (Number.isNaN(value)) return;

          const payload = {
            rotation_type: rotationType,
            group_number: groupNum,
            crop_uuid: cropUuid,
            weight: value,
          };

          if (uuid) {
            weightsToUpdate.push({
              cover_crop_rotation_weight_uuid: uuid,
              ...payload,
            });
          } else {
            weightsToCreate.push(payload);
          }
        });
      });

      // Send bulk updates (rules + cover crop weights)
      await apiClient.put("/rotation-family-rules/bulk", {
        rules_to_create: rulesToCreate,
        rules_to_update: rulesToUpdate,
        rule_uuids_to_delete: ruleUuidsToDelete,
      });

      await apiClient.put("/cover-crop-rotation-weights/bulk", {
        weights_to_create: weightsToCreate,
        weights_to_update: weightsToUpdate,
        weight_uuids_to_delete: weightUuidsToDelete,
      });

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      
      // Refresh data
      await fetchData();
    } catch (err) {
      console.error("Error saving rules:", err);
      setError(err.response?.data?.detail || "Failed to save rotation family rules");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="rotation-type-label">Rotation Type</InputLabel>
          <Select
            labelId="rotation-type-label"
            id="rotation-type"
            value={rotationType}
            label="Rotation Type"
            onChange={handleRotationTypeChange}
          >
            <MenuItem value="three_year">3 Year Rotation</MenuItem>
            <MenuItem value="four_year">4 Year Rotation</MenuItem>
            <MenuItem value="six_year">6 Year Rotation</MenuItem>
          </Select>
        </FormControl>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          className="vintage-button"
        >
          {saving ? "Saving..." : "Save Rules"}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(false)}>
          Rotation family rules saved successfully!
        </Alert>
      )}

      <TableContainer component={Paper} className="table-container">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Crop Family / Crop</TableCell>
              {Array.from({ length: maxGroups[rotationType] }, (_, i) => i + 1).map(
                (groupNum) => (
                  <TableCell key={groupNum}>
                    <TextField
                      size="small"
                      value={groupNames[groupNum] || `Group ${groupNum}`}
                      onChange={(e) => handleGroupNameChange(groupNum, e.target.value)}
                      placeholder={`Group ${groupNum}`}
                      sx={{ width: "100%" }}
                    />
                  </TableCell>
                )
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {cropFamilies.map((family) => {
              const familyCrops = getFamilyCrops(family.crop_family_uuid);
              const isExpanded = expandedFamilies[family.crop_family_uuid] === true;
              
              return (
                <React.Fragment key={family.crop_family_uuid}>
                  {/* Family Row */}
                  <TableRow>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleFamilyExpanded(family.crop_family_uuid);
                          }}
                          disabled={familyCrops.length === 0}
                        >
                          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {family.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    {Array.from({ length: maxGroups[rotationType] }, (_, i) => i + 1).map(
                      (groupNum) => (
                        <TableCell key={groupNum}>
                          <Checkbox
                            checked={areAllFamilyCropsInGroup(family.crop_family_uuid, groupNum)}
                            indeterminate={isFamilyPartiallyChecked(family.crop_family_uuid, groupNum)}
                            onChange={() => handleFamilyCheckboxChange(family.crop_family_uuid, groupNum)}
                            disabled={familyCrops.length === 0}
                          />
                        </TableCell>
                      )
                    )}
                  </TableRow>
                  
                  {/* Expanded Crops Rows */}
                  {isExpanded && familyCrops.map((crop) => (
                    <TableRow key={crop.crop_uuid} sx={{ backgroundColor: "rgba(0, 0, 0, 0.02)" }}>
                      <TableCell sx={{ pl: 6 }}>
                        <Typography variant="body2" sx={{ fontStyle: "italic" }}>
                          {crop.name}
                        </Typography>
                      </TableCell>
                      {Array.from({ length: maxGroups[rotationType] }, (_, i) => i + 1).map(
                        (groupNum) => (
                          <TableCell key={groupNum}>
                            <Checkbox
                              checked={rules[crop.crop_uuid] === groupNum}
                              onChange={() => handleCropCheckboxChange(crop.crop_uuid, groupNum)}
                            />
                          </TableCell>
                        )
                      )}
                    </TableRow>
                  ))}
                </React.Fragment>
              );
            })}

            {/* Separator bar (after families, incl. Ukendt familie) */}
            <TableRow>
              <TableCell colSpan={1 + maxGroups[rotationType]}>
                <Box sx={{ py: 1 }}>
                  <Box
                    sx={{
                      height: 8,
                      borderRadius: 999,
                      backgroundColor: "rgba(0,0,0,0.08)",
                      width: "100%",
                    }}
                  />
                </Box>
              </TableCell>
            </TableRow>

            {/* Cover crops header row (collapsible like families) */}
            <TableRow>
              <TableCell>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleCoverCropsExpanded();
                    }}
                    disabled={coverCrops.length === 0}
                  >
                    {isCoverCropsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    Cover crops
                  </Typography>
                </Box>
              </TableCell>
              {Array.from({ length: maxGroups[rotationType] }, (_, i) => i + 1).map((groupNum) => (
                <TableCell key={groupNum} />
              ))}
            </TableRow>

            {/* Cover crop weight rows (use the same group columns) */}
            {isCoverCropsExpanded &&
              coverCrops.map((crop) => (
                <TableRow key={crop.crop_uuid} sx={{ backgroundColor: "rgba(0, 0, 0, 0.02)" }}>
                  <TableCell sx={{ pl: 6 }}>
                    <Typography variant="body2" sx={{ fontStyle: "italic" }}>
                      {crop.name}
                    </Typography>
                  </TableCell>
                  {Array.from({ length: maxGroups[rotationType] }, (_, i) => i + 1).map((groupNum) => {
                    const entry = coverCropWeights?.[crop.crop_uuid]?.[groupNum];
                    return (
                      <TableCell key={groupNum}>
                        <TextField
                          size="small"
                          type="number"
                          value={entry?.value ?? ""}
                          onChange={(e) => {
                            const next = e.target.value;
                            setCoverCropWeights((prev) => ({
                              ...prev,
                              [crop.crop_uuid]: {
                                ...(prev?.[crop.crop_uuid] || {}),
                                [groupNum]: {
                                  uuid: entry?.uuid ?? null,
                                  value: next,
                                },
                              },
                            }));
                          }}
                          inputProps={{ min: -10, max: 10, step: 1 }}
                          sx={{ width: "100%" }}
                        />
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}

            {isCoverCropsExpanded && coverCrops.length === 0 && (
              <TableRow>
                <TableCell colSpan={1 + maxGroups[rotationType]}>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    No cover crops found.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default RotationFamilyRulesManager;
